import {Injectable} from '@angular/core';
import {Observable, of} from 'rxjs';
import {HTTPS_REQUEST_OPTIONS} from '@constants/https-request-options/https-request-options';
import { HttpClient } from '@angular/common/http';
import {ApiLocationService} from '@services/api-location/api-location.service';
import {MachineCategory} from '@models/adminstration/portal-setup/machine-category/machine-category';
import {shareReplay} from 'rxjs/operators';
import {
  MachineCategoryColumnConfiguration
} from '@models/adminstration/portal-setup/machine-category/machine-category-column-configuration';
import {DashboardMachine} from '@models/dashboard/dashboard-machine';
import {Machine} from '@models/machine/machine';

@Injectable({
  providedIn: 'root'
})
export class MachineCategoryService {

  private MACHINE_CATEGORY_LIST_URL_SUFFIX = '/api/web/admin/machineCategory/list';
  private MACHINE_CATEGORY_GET_URL_SUFFIX = '/api/web/admin/machineCategory/get/:id';
  private MACHINE_CATEGORY_GET_BY_NAME_URL_SUFFIX = '/api/web/admin/machineCategory/getByName/:name';
  private MACHINE_CATEGORY_SAVE_URL_SUFFIX = '/api/web/admin/machineCategory/save';
  private MACHINE_CATEGORY_CREATE_URL_SUFFIX = '/api/web/admin/machineCategory/create';
  private MACHINE_CATEGORY_DELETE_URL_SUFFIX = '/api/web/admin/machineCategory/delete/:id';
  private MACHINE_CATEGORY_COLUMN_CONFIG_GET_URL_SUFFIX = '/api/web/admin/machineCategory/getConfigByCategory/:id';
  private COLUMN_CONFIG_LIST_URL_SUFFIX = '/api/web/admin/machineCategory/listConfigurations';
  private readonly BASE_URL: string;
  private machineCategoriesResponseCache = new Map();
  private machineCategoriesMap = new Map();
  private columnConfigMap = new Map();

  constructor(private http: HttpClient,
              private apiLocation: ApiLocationService) {
    this.BASE_URL = this.apiLocation.getLocation();
  }

  public updateColumnConfigCache(id: number | null, columnConfig: MachineCategoryColumnConfiguration): void {
    if (typeof id === 'number') {
      let url: string = this.BASE_URL + this.MACHINE_CATEGORY_COLUMN_CONFIG_GET_URL_SUFFIX;
      url = url.replace(':id', String(id));
      this.columnConfigMap.set(url, columnConfig);
    }
  }

  public listMachineCategories(): Observable<any> {
    const url: string = this.BASE_URL + this.MACHINE_CATEGORY_LIST_URL_SUFFIX;
    const machineCategoriesFromCache = this.machineCategoriesResponseCache.get(url);
    if (machineCategoriesFromCache) {
      return of(machineCategoriesFromCache);
    }
    const response = this.http.get<MachineCategory[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
    response.subscribe((result) => {
      this.machineCategoriesResponseCache.set(url, result);
    });
    return response;
  }

  public getMachineCategoryById(id: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_CATEGORY_GET_URL_SUFFIX;
    url = url.replace(':id', String(id));
    const machineCategoryFromCache = this.machineCategoriesMap.get(url);
    if (machineCategoryFromCache) {
      return of(machineCategoryFromCache);
    }
    const response = this.http.get<MachineCategory>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
    response.subscribe((result) => {
      this.machineCategoriesMap.set(url, result);
    });
    return response;
  }

  public getMachineCategoryByName(categoryName: string): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_CATEGORY_GET_BY_NAME_URL_SUFFIX;
    url = url.replace(':name', categoryName);
    const machineCategoryFromCache = this.machineCategoriesMap.get(url);
    if (machineCategoryFromCache) {
      return of(machineCategoryFromCache);
    }
    const response = this.http.get<MachineCategory>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
    response.subscribe((result) => {
      this.machineCategoriesMap.set(url, result);
    });
    return response;
  }

  public createMachineCategory(columnConfig: MachineCategoryColumnConfiguration): Observable<any> {
    this.removeUnneededProperties(columnConfig);
    this.clearCategoryCaches();
    const url: string = this.BASE_URL + this.MACHINE_CATEGORY_CREATE_URL_SUFFIX;
    return this.http.post(url, columnConfig, HTTPS_REQUEST_OPTIONS);
  }

  public saveMachineCategory(columnConfig: MachineCategoryColumnConfiguration): Observable<any> {
    this.removeUnneededProperties(columnConfig);
    this.clearCategoryCaches();
    const url: string = this.BASE_URL + this.MACHINE_CATEGORY_SAVE_URL_SUFFIX;
    return this.http.post(url, columnConfig, HTTPS_REQUEST_OPTIONS);
  }

  public deleteMachineCategory(id: number): Observable<any> {
    this.clearCategoryCaches();
    let url: string = this.BASE_URL + this.MACHINE_CATEGORY_DELETE_URL_SUFFIX;
    url = url.replace(':id', String(id));
    return this.http.delete(url, HTTPS_REQUEST_OPTIONS);
  }

  public getColumnConfigurationById(id: number): Observable<any> {
    let url: string = this.BASE_URL + this.MACHINE_CATEGORY_COLUMN_CONFIG_GET_URL_SUFFIX;
    url = url.replace(':id', String(id));
    const configFromCache = this.columnConfigMap.get(url);
    if (configFromCache) {
      return of(configFromCache);
    }
    const response = this.http.get<MachineCategoryColumnConfiguration>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
    response.subscribe((result) => {
      this.columnConfigMap.set(url, result);
    });
    return response;
  }

  public listColumnConfigurations(): Observable<any> {
    const url: string = this.BASE_URL + this.COLUMN_CONFIG_LIST_URL_SUFFIX;
    return this.http.get<MachineCategoryColumnConfiguration[]>(url, HTTPS_REQUEST_OPTIONS).pipe(shareReplay({
      bufferSize: 1,
      refCount: true,
    }));
  }

  public findMachineCategoryConfiguration(machineCategories: MachineCategoryColumnConfiguration[], machine: DashboardMachine | Machine): MachineCategoryColumnConfiguration | undefined {
    if (machine.machineCategoryId && machine.machineCategoryId > 0) {
      return machineCategories.find(category => category.id === machine.machineCategoryId);
    } else {
      return machineCategories.find(category => category.categoryName === machine.category);
    }
  }

  public findMachineCategory(machineCategories: MachineCategory[], machine: DashboardMachine | Machine): MachineCategory | undefined {
    if (machine.machineCategoryId && machine.machineCategoryId > 0) {
      return machineCategories.find(category => category.id === machine.machineCategoryId);
    } else {
      return machineCategories.find(category => category.name === machine.category);
    }
  }

  private clearCategoryCaches(): void {
    this.machineCategoriesResponseCache = new Map();
    this.machineCategoriesMap = new Map();
    this.columnConfigMap = new Map();
  }

  private removeUnneededProperties(columnConfig: MachineCategoryColumnConfiguration) {
    for (const col of columnConfig.categoryColumns) {
      delete col['collapsed'];
      delete col['invalid'];
    }
    delete columnConfig.isCollapsed;
    delete columnConfig.machinesCount;
  }
}
